import { computed, ref, watch } from 'vue';
import { isAxiosError } from 'axios';
import {
  AlertRuleDetailV9,
  AlertRuleExternalDetailV9,
  LoggingCondition,
  NotificationConfigInfoV9,
  ResponseEventConditionV9,
  TagItem,
} from '@/api-client/alert/model';
import {
  ALERT_LEVEL,
  ALERT_TYPE_VALUE,
  EVENT_NAME_ENUM,
  EVENT_TYPE_ENUM,
  EVENT_VARIABLE_CONDITION_ITEMS,
  getAggregationItems,
  getAlertTypeItems,
  getCheckHoldTimeItems,
  getDuringTimeItems,
  getEventTypeItems,
  getEventNameItems,
  getWeeklyScheduleItems,
  RULE_CRITERIA_ITEMS,
  FILE_CHANGE_REVERSE_MAP,
  getFileTypeItems,
  FILE_CHANGE_MAP,
  getFileNoChangeUnitItems,
  getCheckIntervalItems,
  EXTERNAL_ALERT_TYPE_LIST,
  TARGET_KIND_LOCALE_MAP,
  normalizeTargetKindForDetail,
  CHECK_BY_COMBINATION,
  CONDITION_TYPE_CAPTION_MAP,
} from '@/alert/utils/define';
import { AlertRuleV9RequestSubType } from '@/api-client/metav8Over/model/alert-rule-v9-request-sub-type';
import {
  resolveAlertMetricSubType,
  type AlertMetricSubType,
} from '@/alert/utils/alertMetricSubType.utils';
import {
  getConditionsNameByUserAlertType,
  getDisplayNameFromSelectboxItem,
} from '@/alert/utils/utils';
import { buildThresholdEvaluateValueParts } from '@/alert/utils/thresholdDisplay.utils';
import { CustomColumn, SelectboxItem } from '@/common/utils/types';
import {
  convertWeeklySchedule,
  getEventName,
  getMetricSubset,
  parseProcessDownPattern,
  convertEventType,
  convertCheckInterval,
  getHoldTimeName,
} from '@/config/views/userAlert/alertRuleWindow.uses';
import { getTimezoneItemsByLocale, numberWithComma } from '@/common/utils/commonUtils';
import {
  AlertDetailEmit,
  AlertDetailTabProps,
  AlertExternalType,
  TableAlertItem,
} from '@/alert/components/alertDetail/alertDetail.types';
import { TARGET_TYPE, useAlertTagsAndTargets } from '@/alert/utils/tagsAndTargets.uses';
import { AlertTargetItem } from '@/alert/components/alertTargetList/alertTargetList.types';
import { useAlertTargetDetail } from '@/alert/components/alertTargetList/alertTargetDetail.use';
import { isEmpty, startCase, upperFirst, words } from 'lodash-es';
import { i18n, useInternational } from '@/common/locale';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { EventType, UserAlertType } from '@/config/views/userAlert/alertRuleWindow.types';
import { useReasonData } from '@/config/components/reasonSelect/reasonSelect.uses';
import { useAlertDetailStore } from '@/alert/stores/alert-detail';
import { storeToRefs } from 'pinia';
import { parsePostPeriod } from '@/config/views/userAlert/alertRuleDataConditions/alertPostPeriod.setup';
import { CATEGORIES as PROCESS_DOWN_CATEGORIES } from '@/config/views/userAlert/alertRuleDataConditions/alertRuleDataConditionEventProcess.define';
import {
  getEventNameKeys,
  getEventTypeKeys,
} from '@/config/views/userAlert/alertRuleDataConditions/alertRuleDataConditionEventServerChange.define';
import { makeRows } from '@/worker/utils';
import { ALERT_CONTROL_TYPE } from '@/config/views/userAlert/alertRuleDataCommon/alertRuleDataCommon.define';
import { getFilterItemsByGridColumns } from '@/common/components/molecules/filterSearch/filterSearch.utils';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import {
  useGetV9AlertsAlertRulesByAlertRuleId,
  useGetV9AlertsAlertRulesNotificationByAlertRuleId,
  useGetV9AlertsAlertRulesSystemByAlertRuleId,
} from '@/api-client/alert/queries/alert-rule-v9-controller/alert-rule-v9-controller';
import type { SystemAlertDetail } from '@/api-client/alert/model/system-alert-detail';

export interface RuleItem {
  label:
    | string
    | {
        text: string;
        badge: string;
      };
  value: string | number | string[] | AlertTargetItem[] | TableAlertItem;
  key?: string;
  type?: string;
  renderer?: string;
  selectItems?: SelectboxItem[];
}

export type RuleInfo = Record<string, RuleItem>;

const getVariableConditions = (data: Record<string, string[]>): RuleInfo =>
  Object.keys(data).reduce((acc, key) => {
    acc[`${key}`] = {
      label: words(key)
        .map((v) => upperFirst(v))
        .join(' '),
      value: EVENT_VARIABLE_CONDITION_ITEMS[key]
        ? data[key].map((v) =>
            getDisplayNameFromSelectboxItem(EVENT_VARIABLE_CONDITION_ITEMS[key], v),
          )
        : data[key],
    };
    return acc;
  }, {} as RuleInfo);

const useCommonRule = (t) => {
  const getInitData = (): RuleInfo[] => [
    {
      alertRuleType: {
        label: t('WORD.TYPE'),
        type: 'selectbox',
        value: '',
        selectItems: getAlertTypeItems({ containExternal: true }),
      },
      ruleCriteria: {
        label: t('WORD.RULE_CRITERIA'),
        type: 'selectbox',
        renderer: 'label',
        value: '',
        selectItems: RULE_CRITERIA_ITEMS,
      },
    },
    {
      duringTimeInterval: {
        label: t('WORD.AGGREGATION_SCOPE'),
        type: 'duringTimeInterval',
        value: '',
        selectItems: getDuringTimeItems(),
      },
      checkInterval: {
        label: t('WORD.CHECK_INTERVAL'),
        type: 'checkInterval',
        value: 0,
      },
      checkRepeat: {
        label: t('WORD.CHECK_REPEAT'),
        type: 'number',
        value: 0,
      },
      maximumOccurrences: {
        label: t('WORD.MAXIMUM_OCCURRENCES'),
        type: 'maximumOccurrences',
        value: '',
      },
      checkHoldTime: {
        label: t('WORD.ALARM_CONTROL_DURATION'),
        type: 'alarmControlDuration',
        value: '',
        selectItems: getCheckHoldTimeItems(),
      },
    },
    {
      weeklySchedule: {
        label: t('WORD.ALERT_CHECK_SCHEDULE'),
        type: 'weeklySchedule',
        renderer: 'list',
        value: [],
      },
      time: {
        label: t('WORD.TIME'),
        type: 'time',
        renderer: 'list',
        value: [],
      },
    },
  ];
  const commonRuleData = ref<RuleInfo[]>(getInitData());
  const metricConditionSubType = ref<AlertMetricSubType | undefined>();
  const isMultiMetric = ref(false);
  const isFormula = ref(false);
  const isCheckByCombination = ref(false);

  const valueFormatter = {
    selectbox: ({ value: resVal, fieldInfo: refInfo }): string => {
      if (refInfo?.selectItems) {
        return getDisplayNameFromSelectboxItem(refInfo.selectItems, resVal);
      }
      return `${resVal}`;
    },
    checkInterval: ({ data }): string => {
      const { checkInterval, checkIntervalDisplayUnit } = data;
      const { interval } = convertCheckInterval.forUi(checkInterval, checkIntervalDisplayUnit);

      const unit = getDisplayNameFromSelectboxItem(
        getCheckIntervalItems(),
        checkIntervalDisplayUnit,
      );

      return `${numberWithComma(interval)} ${unit}`;
    },
    weeklySchedule: ({ value: resVal }): string[] => {
      const formattedSchedule: string[] = [];
      const uiData = convertWeeklySchedule.forUi(resVal);
      const weekDays = getWeeklyScheduleItems().map((item) => item.name);
      uiData.dayWeek.forEach((v) => {
        formattedSchedule.push(getDisplayNameFromSelectboxItem(getWeeklyScheduleItems(), v));
      });
      return formattedSchedule.sort((a, b) => weekDays.indexOf(a) - weekDays.indexOf(b));
    },
    time: ({ value: resVal }): string[] => {
      const strArr: string[] = [];
      const uiData = convertWeeklySchedule.forUi(resVal);
      if (uiData.timeZone) {
        strArr.push(getDisplayNameFromSelectboxItem(getTimezoneItemsByLocale(), uiData.timeZone));
      }
      strArr.push(
        `${uiData.range[0] ?? '00:00'} ${uiData.range[1] ? '~' : ''} ${uiData.range[1] ?? '00:00'}`,
      );
      return strArr;
    },
    number: ({ value: resVal }): string => numberWithComma(resVal),
    maximumOccurrences: ({ value: resVal }): string =>
      resVal === -1 ? '∞' : numberWithComma(resVal),
    alarmControlDuration: ({ value: resVal, fieldInfo: refInfo, data }): string => {
      if (resVal === '-1') {
        return t('WORD.NONE_2');
      }

      const alertMode =
        data.alertMode === ALERT_CONTROL_TYPE.ALERT_STOP ? 'WORD.ALERT_STOP' : 'WORD.ALERT_CLEAR';

      if (refInfo?.selectItems) {
        return `${t(alertMode)} (${getDisplayNameFromSelectboxItem(refInfo.selectItems, resVal)})`;
      }
      return `${t(alertMode)} (${resVal})`;
    },
    duringTimeInterval: ({ value: resVal, fieldInfo: refInfo }): string => {
      if (!resVal) {
        return '';
      }
      if (refInfo?.selectItems) {
        return getDisplayNameFromSelectboxItem(refInfo.selectItems, resVal);
      }
      return `${resVal}`;
    },
  };

  const initData = () => {
    commonRuleData.value = getInitData();
    metricConditionSubType.value = undefined;
    isMultiMetric.value = false;
    isFormula.value = false;
    isCheckByCombination.value = false;
  };

  const setCommonRuleData = (
    val: AlertRuleDetailV9,
    userAlertType: Capitalize<UserAlertType | AlertExternalType> = ALERT_TYPE_VALUE.METRIC,
  ) => {
    initData();
    if (isEmpty(val)) {
      return;
    }

    const eventType = val.eventConditions?.[0]?.eventType;
    const isKubernetesEvent = eventType === EVENT_TYPE_ENUM.KUBERNETES;
    const isProcessStatusEvent =
      eventType === EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION ||
      eventType === EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION;

    commonRuleData.value.forEach((item) => {
      Object.entries(item).forEach(([key, refInfo]) => {
        const resKey = key === 'time' ? 'weeklySchedule' : key;
        const resVal =
          key === 'duringTimeInterval'
            ? val.eventConditions?.[0]?.duringTimeInterval
            : val![resKey];

        if (userAlertType === ALERT_TYPE_VALUE.EVENT && key === 'checkInterval') {
          delete item.checkInterval;
        }

        if (
          key === 'duringTimeInterval' &&
          !(isKubernetesEvent && userAlertType === ALERT_TYPE_VALUE.EVENT) &&
          !(isProcessStatusEvent && userAlertType === ALERT_TYPE_VALUE.EVENT)
        ) {
          delete item.duringTimeInterval;
        }

        if (
          (userAlertType === ALERT_TYPE_VALUE.PROMETHEUS ||
            userAlertType === ALERT_TYPE_VALUE.ELASTICSEARCH) &&
          key === 'ruleCriteria'
        ) {
          delete item.ruleCriteria;
        }

        if (userAlertType === ALERT_TYPE_VALUE.EVENT_STATISTICS) {
          if (key === 'checkHoldTime') {
            delete item.checkHoldTime;
          }
          if (key === 'maximumOccurrences') {
            delete item.maximumOccurrences;
          }

          if (key === 'time') {
            item.time.label = t('WORD.CHECK_TIME2');
          }
        }

        // 이벤트/이벤트 통계 타입은 룰 설정 화면과 동일하게 '이벤트 발생 횟수'로 표기 (같은 checkRepeat 값)
        if (
          key === 'checkRepeat' &&
          (userAlertType === ALERT_TYPE_VALUE.EVENT ||
            userAlertType === ALERT_TYPE_VALUE.EVENT_STATISTICS)
        ) {
          item.checkRepeat.label = t('WORD.EVENT_NUMBER_OF_OCCURRENCES');
        }

        refInfo.value =
          refInfo.type && valueFormatter[refInfo.type]
            ? valueFormatter[refInfo.type]({ value: resVal, fieldInfo: refInfo, data: val })
            : resVal;
      });
    });

    if (userAlertType === ALERT_TYPE_VALUE.METRIC) {
      const conditions = val.metricConditions ?? [];
      const resolvedSubType = resolveAlertMetricSubType({
        subType: 'subType' in val && typeof val.subType === 'string' ? val.subType : undefined,
        ruleCriteria: val.ruleCriteria,
        metricConditions: conditions,
      });
      metricConditionSubType.value = resolvedSubType;
      const isCombination = val.ruleCriteria === CHECK_BY_COMBINATION;

      if (resolvedSubType === AlertRuleV9RequestSubType.CUSTOM) {
        isFormula.value = true;
        isMultiMetric.value = false;
        isCheckByCombination.value = false;
      } else if (isCombination) {
        isFormula.value = false;
        isMultiMetric.value = false;
        isCheckByCombination.value = true;
      } else if (resolvedSubType === AlertRuleV9RequestSubType.COMPOUND) {
        isFormula.value = false;
        isMultiMetric.value = true;
        isCheckByCombination.value = false;
      } else if (resolvedSubType === AlertRuleV9RequestSubType.SINGLE) {
        isFormula.value = false;
        isMultiMetric.value = false;
        isCheckByCombination.value = false;
      } else {
        const hasFormula = conditions.some((c) => c.threshold?.conditionType === 'formula');
        const isMulti = conditions.length > 1 && !hasFormula;
        isMultiMetric.value = isMulti && !isCombination;
        isFormula.value = hasFormula;
        isCheckByCombination.value = isCombination;
      }

      const metricConditionType = isCombination
        ? undefined
        : resolvedSubType === AlertRuleV9RequestSubType.CUSTOM
          ? t('WORD.CUSTOM')
          : resolvedSubType === AlertRuleV9RequestSubType.COMPOUND
            ? t('WORD.MULTI_ALERT')
            : resolvedSubType === AlertRuleV9RequestSubType.SINGLE
              ? t('WORD.SINGLE_ALERT')
              : undefined;
      if (metricConditionType) {
        commonRuleData.value.push({
          metricConditionType: {
            label: t('WORD.METRIC_TYPE'),
            renderer: 'label',
            value: metricConditionType,
          },
        });
      }
    }
  };

  return {
    commonRuleData,
    metricConditionSubType,
    isMultiMetric,
    isFormula,
    isCheckByCombination,
    setCommonRuleData,
  };
};

export const formatPostPeriod = (period: null | string) => {
  if (period == null) {
    return i18n.global.t('WORD.NOT_SET');
  }

  const unitLang = {
    m: 'WORD.MINUTES',
    h: 'WORD.HOURS',
    d: 'WORD.DAYS',
  } as const;
  const [value, unit] = parsePostPeriod(period);

  if (!value || !unit) {
    return '';
  }
  return i18n.global.t(unitLang[unit], { value });
};

const getAlertLevelAndPeriod = (level?: string, postPeriod?: string) => {
  const formalPostPeriod = formatPostPeriod(postPeriod ?? null);

  return {
    level: {
      label: i18n.global.t('WORD.ALERT_CRITERIA_LEVEL'),
      value: level ?? '',
    },
    postPeriod: {
      label: i18n.global.t('WORD.ALERT_POSTING_PERIOD'),
      value: formalPostPeriod ? [formalPostPeriod] : [],
    },
  };
};

export const useLoggingConditions = () => {
  const { t } = useInternational();

  // field 를 응답 타입 키로 제약해, 응답에 없는 필드명 바인딩이 컴파일에서 걸리게 한다
  const PATH_COLUMNS: CustomColumn<keyof LoggingCondition>[] = [
    {
      caption: 'Path Name',
      field: 'pathName',
      type: 'string',
      width: 175,
    },
    {
      caption: 'Log Type',
      field: 'logType',
      type: 'string',
      width: 175,
    },
    {
      caption: 'Path',
      field: 'filePath',
      type: 'string',
      width: 175,
    },
    {
      caption: 'Filter',
      field: 'filterDetailValues',
      rendererType: 'chip-cell',
      type: 'string',
    },
    {
      caption: 'Parser',
      field: 'logParserItems',
      type: 'string',
      rendererType: 'chip-cell',
    },
  ] as const;

  const getLoggingConditionsData = (condition: ResponseEventConditionV9) => {
    const { level, postPeriod, loggingCondition } = condition || {};
    const { loggingName } = loggingCondition || {};

    const loggingAdapter = (_loggingCondition: LoggingCondition) => {
      return {
        ..._loggingCondition,
        logParserItems: _loggingCondition.logParserItems?.map((item) => item?.parserPattern),
      };
    };

    return {
      loggingName: {
        label: t('WORD.LOGGING'),
        value: loggingName ? [loggingName] : [],
      },
      path: {
        label: 'Path',
        value: {
          columns: PATH_COLUMNS,
          rows: makeRows(
            !isEmpty(loggingCondition) ? [loggingAdapter(loggingCondition)] : [],
            PATH_COLUMNS,
          ),
        },
      },
      ...getAlertLevelAndPeriod(level, postPeriod),
    };
  };

  return {
    getLoggingConditionsData,
  };
};

const useProcessDownConditions = () => {
  const { t } = useInternational();

  const getFormalPattern = (item: string) => {
    const { category, dataType, pattern } = parseProcessDownPattern(item);

    const categoryForUi = getDisplayNameFromSelectboxItem(PROCESS_DOWN_CATEGORIES, category);

    if (dataType === 'string') {
      return `${categoryForUi} like %${pattern}%`;
    }
    if (dataType === 'regex') {
      return `${categoryForUi} ${pattern}`;
    }

    return '';
  };

  const getProcessDownConditionsData = (condition: ResponseEventConditionV9) => {
    const { level, postPeriod, variableConditionList } = condition || {};
    const { detectedPattern, excludePattern } = variableConditionList?.[0] || {};

    const { level: levelInfo, postPeriod: periodInfo } = getAlertLevelAndPeriod(level, postPeriod);

    return {
      level: levelInfo,
      detectionPatterns: {
        label: {
          text: t('WORD.DETECTION_PATTERNS'),
          badge: 'AND',
        },
        value: (detectedPattern || []).map(getFormalPattern),
        type: 'multi-chip-cell',
      },
      excludedPatterns: {
        label: {
          text: t('WORD.EXCLUDED_PATTERNS'),
          badge: 'OR',
        },
        value: (excludePattern || []).map(getFormalPattern),
        type: 'multi-chip-cell',
      },
      postPeriod: periodInfo,
    };
  };

  return {
    getProcessDownConditionsData,
  };
};

const useConditions = () => {
  const { t } = useInternational();

  const conditionsData = ref<RuleInfo[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const conditionsDataByEvent = ref<Record<string, { label: string; value: any }>>({});
  const { tagsAndTargetsController } = useAlertTagsAndTargets();

  const eventRule = ref<Record<string, RuleItem>>({});
  const eventTypeTargets = ref<RuleInfo>({});
  const eventTypeAdditional = ref<RuleItem[]>([]);
  const externalRuleInfo = ref<RuleInfo>({});

  const { getLoggingConditionsData } = useLoggingConditions();
  const { fetchReason, getReasons } = useReasonData();
  const { getProcessDownConditionsData } = useProcessDownConditions();

  const getEventNameSuffix = (
    eventName: string | null,
    variableConditionList: ResponseEventConditionV9['variableConditionList'],
  ) => {
    if (!eventName || eventName !== EVENT_NAME_ENUM.FILE) {
      return '';
    }

    const [variableCondition] = variableConditionList ?? [{}];
    const { eventName: fileType } = variableCondition ?? {};
    const suffix = getDisplayNameFromSelectboxItem(
      getFileTypeItems(),
      FILE_CHANGE_REVERSE_MAP[fileType?.[0] ?? ''],
    );

    return ` (${suffix})`;
  };

  const getEventNameInfo = (
    condition: ResponseEventConditionV9,
    convertedEventType: string,
  ): Record<string, RuleItem> => {
    const { eventType, variableConditionList } = condition ?? {};
    const variableCondition = variableConditionList?.[0];

    // Kubernetes 타입인 경우 Resource Status Type 표시
    if (convertedEventType === EVENT_TYPE_ENUM.KUBERNETES) {
      const resourceStatusType = variableCondition?.resourceStatusType?.[0];
      if (resourceStatusType) {
        return {
          resourceStatusType: {
            label: t('WORD.RESOURCE_STATUS_TYPE'),
            value: getDisplayNameFromSelectboxItem(
              EVENT_VARIABLE_CONDITION_ITEMS.resourceStatusType,
              resourceStatusType,
            ),
          },
        };
      }
      return {};
    }

    // 일반 이벤트: Event 표시
    const convertedEventName =
      Object.values(EVENT_NAME_ENUM).includes(eventType ?? '') && eventType;
    const eventName = getEventName(variableCondition ?? {}) || convertedEventName || null;

    const eventNameSuffix = getEventNameSuffix(eventName, variableConditionList ?? []);

    return eventType && eventName
      ? {
          eventName: {
            label: t('WORD.EVENT'),
            value:
              getDisplayNameFromSelectboxItem(
                getEventNameItems(convertedEventType as EventType),
                eventName,
              ) + eventNameSuffix,
          },
        }
      : {};
  };

  const getAdditionalEventInfo = (
    condition: ResponseEventConditionV9,
  ): Record<string, RuleItem> => {
    const { variableConditionList } = condition ?? {};
    const [variableCondition] = variableConditionList ?? [{}];
    const { eventName: fileType } = variableCondition ?? {};

    if (fileType?.[0] === FILE_CHANGE_MAP.fileSize) {
      return {
        threshold: {
          label: t('WORD.THRESHOLD2'),
          value: `${t('WORD.FILE_SIZE')} ${variableCondition.fileSizeComparison[0]} ${
            variableCondition.fileSizeValue[0]
          } ${variableCondition.fileSizeUnit[0]}`,
        },
      };
    }

    if (fileType?.[0] === FILE_CHANGE_MAP.fileNoChange) {
      const unit = getDisplayNameFromSelectboxItem(
        getFileNoChangeUnitItems(),
        variableCondition.fileNoChangeUnit[0],
      );
      const threshold = `${variableCondition.fileNoChangeThreshold[0]} ${unit}`;
      return {
        threshold: {
          label: t('WORD.THRESHOLD2'),
          value: t('WORD.NO_CHANGES_PERIOD', { value: threshold }),
        },
      };
    }

    return {};
  };

  const setConditionsData = (
    val: AlertRuleDetailV9['eventConditions'] | AlertRuleDetailV9['metricConditions'],
    userAlertType: Capitalize<UserAlertType | AlertExternalType> = ALERT_TYPE_VALUE.METRIC,
  ) => {
    if (!Array.isArray(val)) {
      return;
    }
    if (userAlertType === ALERT_TYPE_VALUE.METRIC || userAlertType === ALERT_TYPE_VALUE.TABLE) {
      const conditions = (val as AlertRuleDetailV9['metricConditions']) || [];
      conditionsData.value =
        conditions.map((item) => {
          const { targets } = tagsAndTargetsController[TARGET_TYPE.TARGETS](item.targets ?? []);
          const formattedTags =
            item.tags?.map((tag: TagItem) => {
              return {
                ...tag,
                tagName: tag.tagName || tag.tagId,
              };
            }) ?? [];
          const { tags } = tagsAndTargetsController[TARGET_TYPE.TAGS](formattedTags);

          let subsetInfo = {};
          if (getMetricSubset({ category: item.category }) === 'process') {
            subsetInfo = {
              processName: {
                label: t('WORD.PROCESS_NAME'),
                value: item.processName ?? '',
              },
            };
          } else if (userAlertType === ALERT_TYPE_VALUE.TABLE) {
            subsetInfo = {
              columnName: {
                label: t('WORD.COLUMN_NAME'),
                value: startCase(item.checkField ?? ''),
              },
            };
          }
          const {
            aggregation,
            critical,
            criticalComparison,
            warningComparison,
            duringTimeInterval,
            evaluateValue,
            warning,
            alertName,
            formulaAlias,
            conditionType,
            dataIds,
            unit: thresholdUnit,
          } = (item.threshold ?? {}) as NonNullable<typeof item.threshold> & { unit?: string };

          return {
            name: {
              label: conditionType === 'formula' ? t('WORD.FORMULA') : t('WORD.ALERT_STAT_NAME'),
              value: [item.category ?? '', alertName ?? ''],
            },
            ...subsetInfo,
            aggregation: {
              label: t('WORD.TARGET_AGGREGATION'),
              value: getDisplayNameFromSelectboxItem(getAggregationItems(), aggregation),
            },
            critical: {
              label: 'Critical',
              value: critical ?? '',
            },
            warning: {
              label: 'Warning',
              value: warning ?? '',
            },
            warningComparison: {
              label: '',
              value: warningComparison ?? '',
            },
            comparison: {
              label: '',
              value: criticalComparison ?? '>',
            },
            evaluateValue: {
              label: '',
              value: buildThresholdEvaluateValueParts(
                evaluateValue,
                duringTimeInterval,
                t('WORD.DURING_THE'),
              ),
            },
            tags: {
              label: t('WORD.TAG'),
              value: tags,
            },
            targets: {
              label: t('WORD.TARGET'),
              value: targets,
            },
            ...(conditionType === 'formula'
              ? {
                  formulaAlias: {
                    label: t('WORD.ALIAS'),
                    value: formulaAlias ?? '',
                  },
                  conditionType: {
                    label: t('WORD.TYPE'),
                    value: conditionType ?? '',
                  },
                  dataIds: {
                    label: '',
                    value: dataIds ?? [],
                  },
                }
              : {}),
            ...(userAlertType === ALERT_TYPE_VALUE.TABLE
              ? {
                  unit: {
                    label: '',
                    value: thresholdUnit?.trim() ?? '',
                  },
                  alertFilter: {
                    label: `${t('WORD.FILTER')} ${t('WORD.COLUMNS')}`,
                    value: item.alertFilter ?? {},
                  },
                  displayColumns: {
                    label: `${t('WORD.DISPLAY2')} ${t('WORD.COLUMNS')}`,
                    value: item.displayColumns ?? [],
                  },
                }
              : {}),
          };
        }) ?? [];
    } else if (
      userAlertType === ALERT_TYPE_VALUE.EVENT ||
      userAlertType === ALERT_TYPE_VALUE.EVENT_STATISTICS
    ) {
      const conditions = (val as AlertRuleDetailV9['eventConditions']) || [];
      const firstCondition = conditions[0] || {};
      const { targets } = tagsAndTargetsController[TARGET_TYPE.TARGETS](
        firstCondition.targets ?? [],
      );
      const { tags } = tagsAndTargetsController[TARGET_TYPE.TAGS](firstCondition.tags ?? [], {
        type: userAlertType,
      });

      let convertedEventType = convertEventType.forUi(firstCondition.eventType ?? '');

      if (convertedEventType === EVENT_TYPE_ENUM.KUBERNETES_PHASE) {
        convertedEventType = EVENT_TYPE_ENUM.KUBERNETES;
      }

      eventRule.value = {
        eventType: {
          label: t('WORD.EVENT_TYPE'),
          value: getDisplayNameFromSelectboxItem(getEventTypeItems(), convertedEventType),
          key: firstCondition.eventType,
        },
        ...getEventNameInfo(firstCondition, convertedEventType),
        ...getAdditionalEventInfo(firstCondition),
      };
      eventTypeTargets.value = {
        tags: {
          label: t('WORD.TAG'),
          value: tags,
        },
        targets: {
          label: t('WORD.TARGET'),
          value: targets,
        },
      };
      eventTypeAdditional.value = [];
      conditionsDataByEvent.value = {};
      externalRuleInfo.value = {};

      const eventTypeVal = firstCondition.eventType;

      if (eventTypeVal === EVENT_TYPE_ENUM.LOGGING) {
        const { loggingName, path, level, postPeriod } = getLoggingConditionsData(firstCondition);
        eventTypeTargets.value.loggingName = loggingName;
        conditionsDataByEvent.value = {
          path,
        };
        conditionsData.value = [
          {
            level,
            postPeriod,
          },
        ];
        return;
      }

      if (
        eventTypeVal === EVENT_TYPE_ENUM.KUBERNETES ||
        eventTypeVal === EVENT_TYPE_ENUM.KUBERNETES_PHASE
      ) {
        eventTypeTargets.value.kind = {
          label: 'Kind',
          value: [
            getDisplayNameFromSelectboxItem(
              EVENT_VARIABLE_CONDITION_ITEMS.k8sKind,
              firstCondition.variableConditionList?.[0]?.kind?.[0] ?? '',
            ),
          ],
        };

        // KUBERNETES_PHASE는 Phase Status를 표시, KUBERNETES는 Reason을 표시
        if (eventTypeVal === EVENT_TYPE_ENUM.KUBERNETES_PHASE) {
          conditionsData.value =
            conditions.map((item) => {
              const [itemVariableCondition] = item?.variableConditionList ?? [{}];
              const { level, postPeriod } = getAlertLevelAndPeriod(item.level, item.postPeriod);
              const { eventId: _eventId, ...variableConditionsData } =
                getVariableConditions(itemVariableCondition);

              return {
                level,
                status: {
                  ...variableConditionsData.status,
                  label: t('WORD.PHASE_STATUS'),
                },
                messageText: {
                  label: 'Message Text',
                  value: item.messageText ?? '',
                },
                postPeriod,
              };
            }) ?? [];
        } else {
          conditionsData.value =
            conditions.map((item) => {
              const [itemVariableCondition] = item?.variableConditionList ?? [{}];
              const { level, postPeriod } = getAlertLevelAndPeriod(item.level, item.postPeriod);

              return {
                level,
                eventId: {
                  label: 'Reason',
                  value: getReasons(itemVariableCondition.eventId.map((v) => +v)),
                  type: 'multi-chip-cell',
                },
                messageText: {
                  label: 'Message Text',
                  value: item.messageText ?? '',
                },
                postPeriod,
              };
            }) ?? [];
        }

        return;
      }

      if (eventTypeVal === EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION) {
        const { level, postPeriod, detectionPatterns, excludedPatterns } =
          getProcessDownConditionsData(firstCondition);
        conditionsData.value = [
          {
            level,
            detectionPatterns,
            excludedPatterns,
            postPeriod,
          },
        ];
        return;
      }

      if (eventTypeVal === EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION) {
        const { postPeriod, detectionPatterns, excludedPatterns } =
          getProcessDownConditionsData(firstCondition);
        const threshold = firstCondition as {
          comparison?: string;
          warningThreshold?: number | null;
          criticalThreshold?: number | null;
        };
        conditionsData.value = [
          {
            comparison: { label: '', value: threshold.comparison ?? '>' },
            warning: { label: ALERT_LEVEL.WARNING, value: threshold.warningThreshold ?? '' },
            critical: { label: ALERT_LEVEL.CRITICAL, value: threshold.criticalThreshold ?? '' },
            detectionPatterns,
            excludedPatterns,
            postPeriod,
          },
        ];
        return;
      }

      if (eventTypeVal === EVENT_TYPE_ENUM.APPLICATION) {
        const { level, postPeriod } = getAlertLevelAndPeriod(
          firstCondition.level,
          firstCondition.postPeriod,
        );
        conditionsData.value = [
          {
            level,
            postPeriod,
          },
        ];
        return;
      }

      const firstVariableCondition = firstCondition.variableConditionList?.[0] ?? {};
      if (eventTypeVal === EVENT_TYPE_ENUM.HOST) {
        if (FILE_CHANGE_REVERSE_MAP[firstVariableCondition.eventName?.[0]]) {
          eventTypeAdditional.value.push({
            label: t('WORD.FILE_PATH'),
            value: firstVariableCondition.filePath[0] ?? '',
          });
          conditionsData.value = [
            getAlertLevelAndPeriod(firstCondition.level, firstCondition.postPeriod),
          ];
          return;
        }

        if (firstVariableCondition.eventName?.[0] === EVENT_NAME_ENUM.SERVER_CHANGE) {
          const serverChangeTypeInfo = getEventTypeKeys();
          const serverChangeEventNameInfo = getEventNameKeys();
          const { level, postPeriod } = getAlertLevelAndPeriod(
            firstCondition.level,
            firstCondition.postPeriod,
          );

          const condition: Record<string, RuleItem> = {};

          Object.entries(serverChangeTypeInfo).forEach(([key, item]) => {
            if (firstVariableCondition[item.key]?.length) {
              const eventTypeNameInfo = serverChangeEventNameInfo[key];
              condition[key] = {
                label: item.eventType,
                value: firstVariableCondition[item.key][0].split(',').map((name) => {
                  return eventTypeNameInfo.find(({ value }) => value === name)?.eventName;
                }),
                type: 'multi-chip-cell',
              };
            }
          });

          conditionsData.value = [{ level, ...condition, postPeriod }];
          return;
        }
      }

      conditionsData.value =
        conditions.map((item) => {
          const [variableCondition] = item?.variableConditionList ?? [{}];
          const { level, postPeriod } = getAlertLevelAndPeriod(item.level, item.postPeriod);

          return {
            level,
            messageText: {
              label: 'Message Text',
              value: item.messageText ?? '',
            },
            ...getVariableConditions(variableCondition),
            postPeriod,
          };
        }) ?? [];
    } else if (EXTERNAL_ALERT_TYPE_LIST.includes(userAlertType?.toLowerCase())) {
      const item = val?.[0] as AlertRuleExternalDetailV9 &
        AlertRuleExternalDetailV9['threshold'] & { query: string };
      const {
        critical,
        serverName,
        serverUrl,
        duringTimeInterval,
        evaluateValue,
        warning,
        comparison,
        missingValue,
        replaceWith,
        query,
      } = item || {};

      conditionsData.value = [
        {
          query: {
            label: 'Query',
            value: query,
          },
          critical: {
            label: 'Critical',
            value: critical ?? '',
          },
          warning: {
            label: 'Warning',
            value: warning ?? '',
          },
          comparison: {
            label: '',
            value: comparison ?? '>',
          },
          evaluateValue: {
            label: '',
            value: buildThresholdEvaluateValueParts(
              evaluateValue,
              duringTimeInterval,
              t('WORD.DURING_THE'),
            ),
          },
          serverName: {
            label: 'Server Name',
            value: serverName ?? '',
          },
          serverUrl: {
            label: 'Server URL',
            value: serverUrl ?? '',
          },
          missingValue: {
            label: 'Missing Value',
            value: missingValue ?? '',
          },
          replaceWith: {
            label: 'Replace With',
            value: replaceWith ?? '',
          },
        },
      ];
    }
  };

  return {
    conditionsData,
    conditionsDataByEvent,
    eventRule,
    eventTypeTargets,
    eventTypeAdditional,
    externalRuleInfo,
    setConditionsData,
    fetchReason,
  };
};

const useSystemAlertDetail = () => {
  const { t } = useInternational();

  const systemAlertDetails = ref<SystemAlertDetail[]>([]);
  const systemAlertPlatform = ref<string>('');
  const systemAlertRuleCriteria = ref<string>('');

  const SYSTEM_ALERT_BASE_COLUMNS: CustomColumn[] = [
    {
      caption: 'Target',
      field: 'targetName',
      type: 'string',
      clickable: true,
      searchable: true,
      defaultSearchField: true,
    },
    {
      caption: 'Alias',
      field: 'targetAlias',
      type: 'string',
      searchable: true,
      defaultSearchField: true,
    },
    {
      caption: 'Duration',
      field: 'duration',
      type: 'string',
      searchable: true,
    },
    {
      caption: 'Recovery Check',
      field: 'checkInterval',
      type: 'string',
      searchable: true,
    },
    {
      caption: 'Maximum Occurrences',
      field: 'maximumOccurrences',
      type: 'string',
      searchable: true,
    },
    {
      caption: 'Alert Control Duration',
      field: 'checkHoldTime',
      type: 'string',
      searchable: true,
    },
  ];

  // conditionType(icmp/agentDisconnected/upDown...)은 상태 on/off 조건을 쓰는 시스템 룰에서만 내려오며,
  // 룰 단위로 한 종류만 내려오므로 컬럼도 1개만 동적으로 붙인다
  const conditionTypeColumn = computed<CustomColumn | null>(() => {
    const conditionType = systemAlertDetails.value.find(
      (item) => item.conditionType,
    )?.conditionType;
    if (!conditionType) return null;
    return {
      caption: CONDITION_TYPE_CAPTION_MAP[conditionType] ?? startCase(conditionType),
      // field 를 conditionType 별로 구분해야 filterSearch·컬럼 설정이 룰 간에 섞이지 않는다.
      // (storageKey 는 화면당 1개이므로 field 가 같으면 다른 conditionType 룰에 필터가 되살아남)
      field: `conditionValue_${conditionType}`,
      type: 'string',
      width: 120,
      searchable: true,
    };
  });

  const systemAlertColumns = computed<CustomColumn[]>(() =>
    conditionTypeColumn.value
      ? [...SYSTEM_ALERT_BASE_COLUMNS, conditionTypeColumn.value]
      : SYSTEM_ALERT_BASE_COLUMNS,
  );

  const systemAlertGridSearchBarOption = computed(() => ({
    use: true,
    filterSearch: {
      use: true,
      storageKey: USER_ENV_KEYS.FILTERSEARCH_alert_alertDetail_systemAlert,
      filterItems: getFilterItemsByGridColumns(systemAlertColumns.value),
    },
  }));

  const formatValueWithUnit = (value?: number, unit?: string): string => {
    if (value == null) return '';
    const unitName =
      getCheckIntervalItems().find((item) => item.value === unit)?.name ?? unit ?? '';
    return `${numberWithComma(value)} ${unitName}`;
  };

  const formatCheckHoldTime = (holdTime?: string, alertMode?: number): string => {
    if (holdTime == null || alertMode == null) return t('WORD.NOT_SET');
    const modeName =
      alertMode === ALERT_CONTROL_TYPE.ALERT_CLEAR ? t('WORD.ALERT_CLEAR') : t('WORD.ALERT_STOP');
    const timeName = getHoldTimeName(holdTime);
    return `${modeName} (${timeName})`;
  };

  const formatConditionValue = (conditionValue?: boolean): string => {
    if (conditionValue == null) return '';
    return conditionValue ? t('WORD.USE') : t('WORD.UNUSED');
  };

  const systemAlertRows = computed(() =>
    systemAlertDetails.value.map((item) => [
      item.targetName ?? '',
      item.targetAlias ?? '',
      formatValueWithUnit(item.condition?.duration, item.condition?.durationUnit),
      formatValueWithUnit(item.condition?.interval, item.condition?.intervalUnit),
      item.condition?.maximum === -1 ? '∞' : numberWithComma(item.condition?.maximum ?? '∞'),
      formatCheckHoldTime(item.condition?.holdTime, item.condition?.alertMode),
      ...(conditionTypeColumn.value ? [formatConditionValue(item.conditionValue)] : []),
    ]),
  );

  const setSystemAlertDetails = (data: SystemAlertDetail[]) => {
    systemAlertDetails.value = data;
    if (data.length > 0) {
      const targetKind = data[0].targetKind ?? '';
      const localeKey = TARGET_KIND_LOCALE_MAP[targetKind];
      systemAlertPlatform.value = localeKey ? t(localeKey) : startCase(targetKind);
      systemAlertRuleCriteria.value = data[0].ruleCriteria ?? '';
    } else {
      systemAlertPlatform.value = '';
      systemAlertRuleCriteria.value = '';
    }
  };

  const clearSystemAlertDetails = () => {
    systemAlertDetails.value = [];
    systemAlertPlatform.value = '';
    systemAlertRuleCriteria.value = '';
  };

  return {
    systemAlertDetails,
    systemAlertPlatform,
    systemAlertRuleCriteria,
    systemAlertColumns,
    systemAlertRows,
    systemAlertGridSearchBarOption,
    setSystemAlertDetails,
    clearSystemAlertDetails,
  };
};

const useNotification = () => {
  const notificationData = ref<
    (NotificationConfigInfoV9['notificationItems'] | NotificationConfigInfoV9['groupInfo'])[]
  >([]);

  const setNotificationData = (val: NotificationConfigInfoV9[]) => {
    const notificationInfoItems = val?.map((item) => {
      const { groupId, groupName } = item.groupInfo || {};
      return {
        groupId,
        groupName,
        schedule: item?.schedule,
        consolidated: item?.consolidated,
        ...(item?.notificationItems || {}),
      };
    });
    notificationData.value = notificationInfoItems.length ? notificationInfoItems : [{}];
  };

  return {
    notificationData,
    setNotificationData,
  };
};

export const setup = (props: AlertDetailTabProps, emit: AlertDetailEmit) => {
  const { t } = useInternational();
  const {
    selectedRuleInfo: ruleInfo,
    userAlertInformationData,
    apiError,
  } = storeToRefs(useAlertDetailStore());

  // Alert Rule Query
  const currentAlertRuleId = ref<string>('');

  // AbortController for alert rule data
  const alertRuleAbortController = ref<AbortController | null>(null);

  const { error: alertRuleError, refetch: refetchAlertRule } =
    useGetV9AlertsAlertRulesByAlertRuleId(currentAlertRuleId, {
      query: {
        enabled: false,
        select: (response) => response?.data?.data?.[0] || null,
      },
      request: {
        params: {
          frameName: FRAME_NAMES.ALERT_DETAIL.INFORMATION,
        },
      },
    });

  // Notification Query
  const currentNotificationAlertRuleId = ref<string>('');
  const currentNotificationTargetId = ref<string | undefined>(undefined);
  const notificationParams = computed(() => ({
    targetId: currentNotificationTargetId.value,
  }));

  const { data: notificationDataResponse, refetch: refetchNotification } =
    useGetV9AlertsAlertRulesNotificationByAlertRuleId(
      currentNotificationAlertRuleId,
      notificationParams,
      {
        query: {
          enabled: false,
          select: (response) => response?.data?.data || [],
        },
        request: {
          params: {
            frameName: FRAME_NAMES.ALERT_DETAIL.NOTIFICATION,
          },
        },
      },
    );

  // System Alert Rule Query
  const currentSystemAlertRuleId = ref<string>('');

  const { refetch: refetchSystemAlertRule } = useGetV9AlertsAlertRulesSystemByAlertRuleId(
    currentSystemAlertRuleId,
    {
      query: {
        enabled: false,
        select: (response) => response?.data?.data ?? [],
      },
      request: {
        params: {
          frameName: FRAME_NAMES.ALERT_DETAIL.INFORMATION,
        },
      },
    },
  );

  const { commonRuleData, isMultiMetric, isFormula, isCheckByCombination, setCommonRuleData } =
    useCommonRule(t);
  const {
    conditionsData,
    conditionsDataByEvent,
    eventRule,
    eventTypeTargets,
    eventTypeAdditional,
    externalRuleInfo,
    setConditionsData,
    fetchReason,
  } = useConditions();
  const { notificationData, setNotificationData } = useNotification();
  const {
    systemAlertDetails,
    systemAlertPlatform,
    systemAlertRuleCriteria,
    systemAlertColumns,
    systemAlertRows,
    systemAlertGridSearchBarOption,
    setSystemAlertDetails,
    clearSystemAlertDetails,
  } = useSystemAlertDetail();
  const { setTypeDetail } = useAlertTargetDetail();

  const userAlertType = ref<Capitalize<UserAlertType | AlertExternalType>>(ALERT_TYPE_VALUE.METRIC);

  const initData = () => {
    setCommonRuleData({});
    setConditionsData([]);
    setNotificationData([]);
    clearSystemAlertDetails();
  };
  const dataSettingController = {
    user: async () => {
      if (!ruleInfo.value.alertRuleId) {
        initData();
        return;
      }

      const setExternalData = async (rowData: AlertRuleExternalDetailV9) => {
        const type =
          (rowData?.alertRuleType as Capitalize<UserAlertType | AlertExternalType>) ??
          ALERT_TYPE_VALUE.PROMETHEUS;
        const conditions = [
          {
            ...rowData.threshold,
            serverName: rowData.serverName,
            serverUrl: rowData.serverUrl,
          },
        ];

        userAlertType.value = type;
        setCommonRuleData(rowData, type);
        setConditionsData(conditions, type);
        currentNotificationAlertRuleId.value = rowData.alertRuleId!;
        currentNotificationTargetId.value = undefined;

        await refetchNotification();
        setNotificationData(notificationDataResponse.value ?? []);
      };

      /**
       * 내부 알림 규칙 데이터 설정
       */
      const setInternalData = async (rowData: AlertRuleDetailV9) => {
        const type =
          (rowData?.alertRuleType as Capitalize<UserAlertType | AlertExternalType>) ??
          ALERT_TYPE_VALUE.METRIC;
        const conditions = rowData[getConditionsNameByUserAlertType(type)];

        if (conditions?.[0]?.eventType === EVENT_TYPE_ENUM.KUBERNETES) {
          const kind = conditions[0]?.variableConditionList[0]?.kind[0];
          await fetchReason(kind);
        }

        userAlertType.value = type;
        setCommonRuleData(rowData, type);
        setConditionsData(conditions, type);

        currentNotificationAlertRuleId.value = rowData.alertRuleId!;
        currentNotificationTargetId.value = undefined;

        await refetchNotification();
        setNotificationData(notificationDataResponse.value ?? []);
      };

      /**
       * 알림 규칙 데이터 설정 - 조건부 타입 적용
       */
      const setData = async (_rowData: AlertRuleDetailV9 | AlertRuleExternalDetailV9) => {
        const isExternal = EXTERNAL_ALERT_TYPE_LIST.includes(
          (_rowData?.alertRuleType as string)?.toLocaleLowerCase() ?? '',
        );

        // 조건부 타입을 사용하여 타입 안전성 확보
        if (isExternal) {
          await setExternalData(_rowData as AlertRuleExternalDetailV9);
        } else {
          await setInternalData(_rowData as AlertRuleDetailV9);
        }
      };

      if (
        userAlertInformationData.value &&
        userAlertInformationData.value.alertRuleId === ruleInfo.value.alertRuleId
      ) {
        await setData(userAlertInformationData.value);
        return;
      }

      currentAlertRuleId.value = ruleInfo.value.alertRuleId;
      const { data } = await refetchAlertRule();

      if (data) {
        await setData(data);
      }
    },
    system: async () => {
      if (!ruleInfo.value.alertRuleId) {
        setNotificationData([]);
        clearSystemAlertDetails();
        return;
      }

      currentSystemAlertRuleId.value = ruleInfo.value.alertRuleId;
      const { data: systemData } = await refetchSystemAlertRule();
      setSystemAlertDetails((systemData as SystemAlertDetail[]) ?? []);

      currentNotificationAlertRuleId.value = ruleInfo.value.alertRuleId;
      currentNotificationTargetId.value = ruleInfo.value.alertTargetId;

      await refetchNotification();
      setNotificationData(notificationDataResponse.value ?? []);
    },
  };

  const setScrollTop = () => {
    const el = document.querySelector('.alert-detail .ev-tabs-body');
    if (el) {
      el.scrollTop = 0;
    }
  };

  const onClickSystemAlertCell = ({ field, row }: { field: string; row: unknown[] }) => {
    if (field !== 'targetName') return;

    const clickedTargetName = row[0] as string;
    const detail = systemAlertDetails.value.find((item) => item.targetName === clickedTargetName);

    if (!detail?.targetId || !detail?.targetKind) return;

    const category = normalizeTargetKindForDetail(detail.targetKind);
    setTypeDetail({
      name: detail.targetName ?? '',
      category,
      targetId: detail.targetId,
    });
  };

  const getListColumnTitle = (val: RuleItem['value']): string => {
    if (Array.isArray(val)) {
      return val.join(' ');
    }
    return `${val}`;
  };

  // Watch alertRuleError
  watch(alertRuleError, (error: unknown) => {
    let errorMessage = '';

    if (isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    emit('api-error', {
      message: errorMessage,
      isError: true,
    });
  });

  watch(
    [() => props.isShow, ruleInfo],
    async ([isShow, currRuleInfo], [, prevRuleInfo]) => {
      if (!isShow) {
        alertRuleAbortController.value?.abort();
        alertRuleAbortController.value = null;
        initData();

        return;
      }

      const controllerAlertRuleType = EXTERNAL_ALERT_TYPE_LIST.includes(
        currRuleInfo.alertRuleType?.toLocaleLowerCase(),
      )
        ? 'user'
        : currRuleInfo.alertRuleType;

      if (controllerAlertRuleType && prevRuleInfo) {
        emit('update:isLoading', true);
        await dataSettingController[controllerAlertRuleType]?.();
        setScrollTop();
        emit('update:isLoading', false);
      }
    },
    { immediate: true, deep: true },
  );

  watch(apiError, (val) => {
    if (val.isError) {
      emit('api-error', val);
    }
  });

  // 프로세스 상태 감지 / 프로세스 수 변동 감지 룰: 정보 탭에 '매칭 프로세스 목록' 섹션 노출
  const isProcessStatusDetection = computed(() => {
    const eventType = userAlertInformationData.value?.eventConditions?.[0]?.eventType;
    return (
      eventType === EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION ||
      eventType === EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION
    );
  });

  return {
    ruleInfo,
    commonRuleData,
    isMultiMetric,
    isFormula,
    isCheckByCombination,
    conditionsData,
    conditionsDataByEvent,
    notificationData,
    userAlertType,
    eventRule,
    eventTypeTargets,
    eventTypeAdditional,
    externalRuleInfo,
    getListColumnTitle,
    systemAlertDetails,
    systemAlertPlatform,
    systemAlertRuleCriteria,
    systemAlertColumns,
    systemAlertRows,
    systemAlertGridSearchBarOption,
    onClickSystemAlertCell,
    isProcessStatusDetection,
    t,
  };
};
